<script setup>
import { ref, onMounted, watch } from 'vue'
import api from '@/api'
import { useI18n } from '@/i18n'
import { useUserStore } from '@/stores/user'

const props = defineProps({ groupId: { type: Number, required: true } })
const { t } = useI18n()
const user = useUserStore()

const loading  = ref(false)
const error    = ref('')
const aliases  = ref([])
const newAlias = ref('')

const columns = [
  { field: 'name', header: t('hackwatch.groupDetail.aliasName'), sortable: false },
  { field: 'ts',   header: t('hackwatch.groupDetail.aliasTime'), sortable: true  },
]

async function load() {
  loading.value = true
  error.value = ''
  try {
    const { data } = await api.getGroupAliases(props.groupId)
    aliases.value = data || []
  } catch (e) {
    console.error(e)
    error.value = t('hackwatch.groupDetail.aliasLoadFailed')
  } finally {
    loading.value = false
  }
}

async function add() {
  if (!newAlias.value.trim()) return
  try {
    await api.createGroupAlias(props.groupId, { name: newAlias.value.trim() })
    newAlias.value = ''
    await load()
  } catch (e) {
    console.error(e)
  }
}

onMounted(load)
watch(() => props.groupId, load)
</script>

<template>
  <div class="hw-card">
    <div class="hw-card-header">
      <i class="pi pi-tag hw-card-icon" />
      <span class="hw-card-title">{{ t('hackwatch.groupDetail.legendAliases') }}</span>
    </div>
    <div v-if="user.can('hackwatch_groups.change')" class="hw-card-body" style="padding-bottom: .5rem;">
      <div class="add-row">
        <InputText v-model="newAlias" :placeholder="t('hackwatch.groupDetail.newAliasPlaceholder')" class="w-14rem" size="small" />
        <pButton :label="t('hackwatch.groupDetail.addAlias')" size="small" @click="add" />
      </div>
    </div>
    <AppTableWidget
      :columns="columns"
      :data="aliases"
      :loading="loading"
      :paginated="true"
      :searchable="false"
      :default-rows="10"
      :rows-options="[10, 25, 50]"
      data-key="id"
      :empty-text="t('hackwatch.groupDetail.noAliases')"
      @refresh="load"
    >
      <template #cell-name="{ row }">
        <Tag :value="row.name" severity="secondary" />
      </template>
      <template #cell-ts="{ row }">{{ new Date(row.ts).toLocaleString() }}</template>
    </AppTableWidget>
    <div v-if="error" class="mt-1 text-sm" style="color: var(--p-danger)">{{ error }}</div>
  </div>
</template>

<style scoped>
.add-row {
  display: flex;
  gap: .5rem;
  margin-bottom: .75rem;
  align-items: center;
}
</style>
